import { readOnly, equal, notEmpty, or } from '@ember/object/computed';
import { computed } from '@ember/object';
import { isBlank, isNone, isPresent } from '@ember/utils';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo } from 'ember-data/relationships';
import { isArray } from '@ember/array';

import actionInfo, { actionTypeName } from 'cio/utils/action-info';
import CustomerModel from 'cio/models/customer';
import CampaignModel from 'cio/models/campaign';
import BroadcastTriggerModel from 'cio/models/broadcast-trigger';
import NewsletterModel from 'cio/models/newsletter';
import TransactionalMessageModel from 'cio/models/transactional-message';
import ActionModel, { ActionType, Platform } from 'cio/models/action';
import TemplateModel from 'cio/models/template';
import SubjectModel from 'cio/models/subject';
import DeliveryDetailModel from 'cio/models/delivery-detail';
import { findFirstNonEmptyTitle } from 'cio/utils/push/custom-payload';

const PUSH_RESERVED_RECIPIENTS = ['all', 'last_used'];

// The order of these arrays is important. To determine the
// current delivery state, we iterate through until we find
// the first non-null attribute.
export const STATES = [
  'holdout',
  'spammed',
  'unsubscribed',
  'converted',
  'clicked',
  'opened',
  'suppressed',
  'bounced',
  'delivered',
  'deferred',
  'sent',
  'failed',
  'undeliverable',
  'attempted',
  'drafted',
  'queued',
] as const;
export const DELIVERY_STATES = [
  'holdout',
  'spammed',
  'suppressed',
  'bounced',
  'delivered',
  'sent',
  'failed',
  'undeliverable',
  'deferred',
  'attempted',
  'drafted',
  'queued',
] as const;
export const END_USER_STATES = ['spammed', 'unsubscribed', 'converted', 'clicked', 'opened'] as const;
// "sent" is omitted because it's the lowest priority/default state
export const CHRONOLOGICAL_STATES = [
  'holdout',
  'suppressed',
  'bounced',
  'delivered',
  'deferred',
  'failed',
  'undeliverable',
  'attempted',
  'drafted',
  'queued',
] as const;

export type PushDeliveryMetaReason = {
  id: string;
  platform: Platform;
  status: number;
  detail: string;
  bounced: boolean;
  suppressed: boolean;
};

// before wide TypeScript usage, bugs happened calling .reverse() on these read-only arrays and modifying them in-place
// freezing them prevents it from happening (still possible otherwise in non-TypeScript files)
Object.freeze(STATES);
Object.freeze(DELIVERY_STATES);
Object.freeze(END_USER_STATES);
Object.freeze(CHRONOLOGICAL_STATES);

interface DeliveryMeta {
  [key: string]: any;

  reason?: string;
  message_limit_retry?: boolean;
}

export default class DeliveryModel extends Model {
  // Delivery state timestamps
  @attr('number') holdout!: number;
  @attr('number') spammed!: number;
  @attr('number') suppressed!: number;
  @attr('number') bounced!: number;
  @attr('number') sent!: number;
  @attr('number') delivered!: number;
  @attr('number') failed!: number;
  @attr('number') deferred!: number;
  @attr('number') drafted!: number;
  @attr('number') attempted!: number;
  @attr('number') undeliverable!: number;
  @readOnly('created') queued!: number;

  @attr('number') campaignId!: number;
  @attr('number') newsletterId!: number;
  @attr('number') transactionalMessageId!: number;

  @belongsTo('customer', { async: true, inverse: 'deliveries' }) customer!: CustomerModel;
  @belongsTo('campaign', { async: true, inverse: null }) campaign!: CampaignModel;
  @belongsTo('broadcast-trigger', { async: true, inverse: null }) broadcastTrigger!: BroadcastTriggerModel;
  @belongsTo('newsletter', { async: true, inverse: null }) newsletter!: NewsletterModel;
  @belongsTo('transactional-message', { async: true, inverse: null }) transactionalMessage!: TransactionalMessageModel;
  @belongsTo('action', { async: true, inverse: null }) action!: ActionModel;
  @belongsTo('template', { async: true, inverse: null }) template!: TemplateModel;
  @belongsTo('subject', { async: true, inverse: null }) journeySubject!: SubjectModel;

  @belongsTo('delivery-detail', { async: true, inverse: 'delivery' }) detail!: DeliveryDetailModel;

  @attr('boolean') customerDeleted!: boolean;
  @attr('boolean') forgotten!: boolean;
  @attr('boolean') canConvert!: boolean;

  @computed(...CHRONOLOGICAL_STATES)
  get _latestChronologicalState() {
    // For holdout deliveries, we want to show their status as Holdout, so that users can differentiate between real messages and holdout tests.
    if (this.holdout != null) {
      return CHRONOLOGICAL_STATES[0];
    }

    return [...CHRONOLOGICAL_STATES]
      .filter((state: (typeof CHRONOLOGICAL_STATES)[number]) => isPresent(this[state]))
      .sort((thisState, nextState) => this[nextState] - this[thisState])[0];
  }

  @computed(...DELIVERY_STATES)
  get deliveryState() {
    return DELIVERY_STATES.find((state: (typeof DELIVERY_STATES)[number]) => this[state]);
  }

  @computed(...END_USER_STATES, '_latestChronologicalState', 'sent')
  get displayState() {
    let endUserState = END_USER_STATES.find((state: (typeof END_USER_STATES)[number]) => this[state]);

    if (endUserState) {
      return endUserState;
    } else if (
      // If there is no chron state or it has been drafted/queued as well as sent, the status is sent
      !this._latestChronologicalState ||
      (['drafted', 'queued'].includes(this._latestChronologicalState) && this.sent && !this.delivered)
    ) {
      return 'sent';
    } else if (
      // If the latest chron state is drafted/queued and the message has been sent and delivered, the status is delivered
      ['drafted', 'queued'].includes(this._latestChronologicalState) &&
      this.sent &&
      this.delivered
    ) {
      return 'delivered';
    }

    return this._latestChronologicalState;
  }

  @equal('deliveryState', 'holdout') isHoldout!: boolean;
  @equal('deliveryState', 'suppressed') isSuppressed!: boolean;
  @equal('deliveryState', 'bounced') isBounced!: boolean;
  @equal('deliveryState', 'sent') isSent!: boolean;
  @equal('deliveryState', 'deferred') isDeferred!: boolean;
  @equal('deliveryState', 'delivered') isDelivered!: boolean;
  @equal('deliveryState', 'failed') isFailed!: boolean;
  @equal('deliveryState', 'drafted') isDrafted!: boolean;
  @equal('deliveryState', 'attempted') isAttempted!: boolean;
  @equal('deliveryState', 'spammed') isSpammed!: boolean;
  @equal('deliveryState', 'undeliverable') isUndeliverable!: boolean;

  @computed(
    'isSent',
    'isSuppressed',
    'isBounced',
    'isUndeliverable',
    'isSpammed',
    'isDelivered',
    'isRedeliverableAttempted',
  )
  get isRedeliverable() {
    return (
      this.isSent ||
      this.isSuppressed ||
      this.isBounced ||
      this.isUndeliverable ||
      this.isSpammed ||
      this.isDelivered ||
      this.isRedeliverableAttempted
    );
  }

  // Attempted deliveries that have exceeded message limits and are scheduled for auto-retry
  @computed('isAttempted', 'meta.message_limit_retry')
  get isRedeliverableAttempted() {
    return this.isAttempted && this.meta?.message_limit_retry != null;
  }

  @notEmpty('sent') hasBeenSent!: boolean;

  @computed('deliveryState')
  get latestTimestamp() {
    if (!this.deliveryState) {
      return;
    }

    return this.get(this.deliveryState);
  }

  @computed('displayState')
  get displayStateTimestamp() {
    return this.get(this.displayState);
  }

  @attr('string') recipient!: string;
  @attr('string') bcc!: string;
  @attr('string') replyTo!: string;
  @attr('string') subject!: string;
  @attr('number') created!: number;
  @attr('number') opened!: number;
  @attr('number') clicked!: number;
  @attr('number') converted!: number;
  @attr('number') unsubscribed!: number;
  @attr('string') failureMessage!: string;
  @attr('string') actionType!: ActionType;
  @attr() meta!: DeliveryMeta;

  @attr('string') customerId!: string;
  @attr('string') customerName!: string;

  pendingDelete = false;
  pendingRetry = false;
  pendingSend = false;

  @or('failureMessage', 'meta.reason', 'meta.failed_message') errorMessage!: string;
  @or('errorMessage', 'detail.hasErrorMessages') hasError!: boolean;

  @computed('subject', 'actionTypeName', 'template.{hasJsonContent,bodyJson,subject}')
  get displaySubject() {
    let detailSubject = ((this as DeliveryModel).belongsTo('detail').value() as DeliveryDetailModel | null)?.subject;
    let title = isBlank(this.subject) ? (isBlank(detailSubject) ? '(No subject)' : detailSubject) : this.subject;

    if (this.actionTypeName === 'push') {
      let { bodyJson, editor } = this.template.getProperties('bodyJson', 'editor');

      if (editor === 'json') {
        title = findFirstNonEmptyTitle(bodyJson);
      }
    }

    return title;
  }

  @computed('actionType')
  get actionTypeName() {
    return actionTypeName(this.actionType);
  }

  @computed('actionType', 'canConvert')
  get actionInfo() {
    return actionInfo(this.actionType, { canConvert: this.canConvert });
  }

  @or('pendingDelete', 'pendingRetry', 'pendingSend') isPending!: boolean;

  @computed('hasBeenSent', 'isPending', 'failed', 'drafted', 'undeliverable')
  get canBeSentOrDeleted() {
    return !this.hasBeenSent && !this.isPending && [this.failed, this.drafted, this.undeliverable].some(isPresent);
  }

  @computed('isDrafted', 'isFailed')
  get label() {
    if (this.isDrafted) {
      return 'draft';
    } else {
      if (this.isFailed) {
        return `failed ${this.actionInfo?.shortName}`;
      } else {
        return this.actionInfo?.shortName;
      }
    }
  }

  @computed('recipient')
  get parsedRecipient() {
    try {
      return JSON.parse(this.recipient);
    } catch (_) {
      return this.recipient;
    }
  }

  @computed('actionType', 'recipient', 'customerId', 'customerName')
  get pushRecipient() {
    let { actionType, customerId, customerName, parsedRecipient } = this;

    if (actionType !== 'push_action') {
      return this.recipient;
    }

    // When the recipient is an email address, it will be:
    //
    // (a) string, and
    // (b) not a reserved push token
    //
    // In that case, we can return the email address itself without further processing.
    if (typeof parsedRecipient === 'string' && !PUSH_RESERVED_RECIPIENTS.includes(parsedRecipient)) {
      return parsedRecipient;
    }

    if (isNone(customerId)) {
      return 'deleted customer';
    }

    return customerName || customerId;
  }

  @computed('actionType', 'customer.devices', 'recipient')
  get recipientDevices() {
    let { actionType, customer, recipient } = this;

    if (actionType === 'push_action') {
      let devices = customer.get('sortedDevices') || [];

      if (recipient === 'last_used') {
        return devices.length > 0 ? [devices[0]] : [];
      }

      if (recipient === 'all') {
        return devices;
      }

      return this.extractTokens(recipient)
        .map((token) => devices.findBy('id', token))
        .filter((device) => !isNone(device));
    }

    return [];
  }

  private extractTokens(recipient: string) {
    try {
      let tokens: string[] | null = JSON.parse(recipient);
      return !isArray(tokens) ? [] : tokens;
    } catch (e) {
      // it's not an array and failed to parse, probably single token
      return [recipient];
    }
  }

  @computed('actionType', 'meta.reason')
  get deviceStatuses() {
    let { actionType, meta } = this;

    if (actionType !== 'push_action' || isNone(meta)) {
      return null;
    }

    try {
      let { reason } = meta as { reason: string };

      return JSON.parse(reason);
    } catch (e) {
      return null;
    }
  }

  @computed('actionType', 'meta.reason')
  get pushPlatforms() {
    let { actionType, meta } = this;

    if (actionType !== 'push_action' || isNone(meta)) {
      return [];
    }

    try {
      let { reason } = meta as { reason: string };

      let parsedReason: PushDeliveryMetaReason[] = JSON.parse(reason);

      return parsedReason
        .reduce<Platform[]>((acc, reason: any) => {
          acc.push(reason.platform);
          return acc;
        }, [])
        .compact()
        .uniq()
        .sort();
    } catch (e) {
      return [];
    }
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    delivery: DeliveryModel;
  }
}
